import React, { Component } from 'react';
import { injectIntl, defineMessages } from 'react-intl';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { withRouter, Link } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Panel } from 'primereact/panel';
import { Column } from 'primereact/column';
import Paginator from '../../components/Paginator';
import { sortUrl, parse, toSortOrder } from '../../utils/UrlUtil';
import SurveyListHeader from './SurveyListHeader';

const messages = defineMessages({
  name: { id: 'attrs.name', defaultMessage: 'name' },
  created: { id: 'attrs.created', defaultMessage: 'Created' },
  templateName: { id: 'attrs.survey_template', defaultMessage: 'Survey Template' },
  status: { id: 'attrs.status', defaultMessage: 'Status' },
});

const SURVEY_STATUS = {
  NEW: 'NEW',
  INVITED: 'INVITED',
  STARTED: 'STARTED',
  SUBMITTED: 'SUBMITTED',
  DECLINED: 'DECLINED',
  EXPIRED: 'EXPIRED',
};

const getSurveyStatus = (statuses) => {
  let totalAssessments = 0;
  Object.keys(SURVEY_STATUS).forEach((item) => {
    if (statuses[item]) {
      totalAssessments += parseInt(statuses[item], 10);
    }
  });
  const sumitted = statuses.SUBMITTED ? parseInt(statuses.SUBMITTED, 10) : 0;
  return {
    htmlString: `${sumitted} / ${totalAssessments}`,
    string: statuses.SUBMITTED === totalAssessments ? 'FINISH' : 'IN_PROGRESS',
  };
};

const surveyStatusStyleClass = (status) => {
  switch (status.string) {
    case 'FINISH': {
      return 'filter filter--success is-active';
    }
    case 'IN_PROGRESS': {
      return 'filter filter--warning is-active';
    }
    default:
      return '';
  }
};

const getSurveyStatusComp = (status) => {
  const styleClass = surveyStatusStyleClass(status);
  return <span className={styleClass}>{status.htmlString}</span>;
};

const nameTemplate = (rowData, { currentAccount, keyField, displayField }) => {
  const detailUrl = `/accounts/${currentAccount}/surveys/${rowData[keyField]}`;
  return (
    <Link to={detailUrl} className="app-link">
      {rowData[displayField]}
    </Link>
  );
};

const timeFieldTemplate = (rowData, { formatDate, field }) => {
  const updated = formatDate(moment(rowData[field]), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
  return updated;
};

class SurveyList extends Component {
  constructor(props) {
    super(props);
    this.onSortChange = this.onSortChange.bind(this);
  }

  onSortChange(event) {
    const { location, history } = this.props;
    sortUrl(event, location, history);
  }

  render() {
    const {
      connection: {
        edges, totalCount, indexFrom, indexTo, pageInfo,
      },
      intl: { formatMessage, formatDate },
      currentAccount,
      loading,
      location,
    } = this.props;
    // data list
    const listRows = edges.map(({ node }) => ({
      ...node,
      language: node.self ? node.self.respondent.locale : '',
      status: getSurveyStatusComp(getSurveyStatus(node.statuses)),
      // status: node.statuses,
      template: node.template ? node.template.title : '',
    }));
    const params = parse(location.search);
    const { sort = {} } = params;
    const { sortBy, sortDir } = sort;

    // header
    const nameHeader = formatMessage(messages.name);
    const createdHeader = formatMessage(messages.created);
    const templateHeader = formatMessage(messages.templateName);
    const statusHeader = formatMessage(messages.status);

    // footer
    const footer = <Paginator totalCount={totalCount} indexFrom={indexFrom} indexTo={indexTo} pageInfo={pageInfo} />;

    return (
      <div className="p-grid">
        <div className="p-col-12">
          <div className="brand-detailed-wrapper">
            <Panel>
              <div className="p-grid">
                <div className="p-col-12">
                  <SurveyListHeader currentAccount={currentAccount} />
                </div>
                <div className="p-col-12 align-center">
                  <DataTable
                    value={listRows}
                    autoLayout
                    responsive
                    sortField={sortBy}
                    sortOrder={toSortOrder(sortDir)}
                    onSort={this.onSortChange}
                    lazy
                    loading={loading}
                  >
                    <Column
                      field="name"
                      header={nameHeader}
                      body={nameTemplate}
                      keyField="id"
                      displayField="name"
                      currentAccount={currentAccount}
                      sortable
                      style={{ textAlign: 'left' }}
                    />
                    <Column
                      field="template"
                      header={templateHeader}
                    />
                    <Column
                      field="status"
                      header={statusHeader}
                    />
                    <Column
                      field="createdOn"
                      header={createdHeader}
                      body={timeFieldTemplate}
                      formatDate={formatDate}
                      sortable
                    />
                  </DataTable>
                </div>
                <div className="p-col-12 align-center">{footer}</div>
              </div>
            </Panel>
          </div>
        </div>
      </div>
    );
  }
}

SurveyList.propTypes = {
  connection: PropTypes.objectOf(PropTypes.any).isRequired,
  currentAccount: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
    formatDate: PropTypes.func,
  }).isRequired,
  location: PropTypes.objectOf(PropTypes.any).isRequired,
  history: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default withRouter(injectIntl(SurveyList));
